import { Button } from '@/styles/buttons';
import { Column } from '@/styles/common';
import {
	SelectCaret,
	SelectClearAll,
	SelectContainer,
	SelectDivider,
	SelectOption,
	SelectOptions,
	SelectOptionsAll,
	SelectValueBadge,
	SelectValues,
} from '@/styles/fields';
import { joinClassNames } from '@/utils/utils';
import React, { useEffect, useRef, useState } from 'react';

export interface ISelectFieldOption {
	value: TValue;
	label: string;
}

type MultiSelectFieldProps = {
	multiple: true;
	value: TValue[];
	setValue: (values: TValue[]) => void;
};

type SingleSelectFieldProps = {
	multiple?: false;
	value?: TValue;
	setValue: (value: TValue | undefined) => void;
};

type SelectFieldProps = {
	options: ISelectFieldOption[];
	disabled?: boolean;
	required?: boolean;
	className?: string;
	label?: string;
	variant?: TVariant;
	title?: string;
	closeWhenSelect?: boolean;
	show?: 'value' | 'label';
	showValueIfOptionDoesntExist?: boolean;
	customRender?: ({
		selectOption,
		isOptionSelected,
		highlightedIndex,
		setHighlightedIndex,
	}: {
		selectOption: (option: TValue) => void;
		isOptionSelected: (option: ISelectFieldOption) => boolean;
		highlightedIndex: number;
		setHighlightedIndex: React.Dispatch<React.SetStateAction<number>>;
	}) => React.ReactNode;
} & (SingleSelectFieldProps | MultiSelectFieldProps);

const SelectField: React.FC<SelectFieldProps> = ({
	value,
	className,
	disabled,
	required,
	label,
	setValue,
	variant,
	title,
	multiple,
	options,
	closeWhenSelect = true,
	show = 'label',
	showValueIfOptionDoesntExist = false,
	customRender,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [highlightedIndex, setHighlightedIndex] = useState(0);
	const containerRef = useRef<HTMLDivElement>(null);
	const optionsRef = useRef<HTMLUListElement>(null);

	const clearOptions = () => {
		multiple ? setValue([]) : setValue(undefined);
	};

	const selectOption = (option: TValue) => {
		if (multiple) {
			if (value.some((v) => v === option)) {
				setValue(value.filter((v) => v !== option));
			} else {
				setValue([...value, option]);
			}
		} else {
			if (option !== value) setValue(option);
		}
	};

	const isOptionSelected = (option: ISelectFieldOption) =>
		multiple ? value.some((v) => v === option.value) : option.value === value;

	const getValue = (value: TValue | undefined) => {
		if (title && value === -1) return title;
		const option = options.find((o) => o.value === value);
		if (!option && showValueIfOptionDoesntExist) return value;
		if (!option) return undefined;
		return show === 'label' ? option.label : option.value;
	};

	useEffect(() => {
		if (isOpen) {
			setHighlightedIndex(0);

			if (containerRef.current && optionsRef.current) {
				const { top } = containerRef.current?.getBoundingClientRect();
				const { height } = optionsRef.current?.getBoundingClientRect();
				if (top + height + 30 > window.innerHeight) {
					optionsRef.current.style.top = `-${height}px`;
				}
			}
		}
	}, [isOpen]);

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (disabled) return;
			if (e.target !== containerRef.current) return;

			switch (e.code) {
				case 'Enter':
				case 'Space':
					setIsOpen((_) => !_);
					if (isOpen) selectOption(options[highlightedIndex].value);
					break;

				case 'ArrowUp':
				case 'ArrowDown':
					if (!isOpen) {
						setIsOpen(true);
						break;
					}
					const newValue = highlightedIndex + (e.code === 'ArrowDown' ? 1 : -1);
					setHighlightedIndex((newValue + options.length) % options.length);

					break;
				case 'Escape':
					setIsOpen(false);
					break;
			}
		};
		containerRef.current?.addEventListener('keydown', handler);

		return () => {
			containerRef.current?.removeEventListener('keydown', handler);
		};
	}, [isOpen, highlightedIndex, options]);

	const _required =
		!disabled && required && (!value || (multiple && value.length === 0));

	return (
		<Column>
			{label && <label>{label}</label>}
			<SelectContainer
				field-type='select'
				ref={containerRef}
				onBlur={(e) => {
					const parent = e.relatedTarget?.parentElement;
					parent?.id !== 'list-actions' && !disabled && setIsOpen(false);
				}}
				onClick={() => {
					!disabled && setIsOpen((_) => !_);
				}}
				tabIndex={0}
				className={joinClassNames(
					className,
					disabled && 'disabled',
					_required && 'required'
				)}
				variant={variant}>
				<SelectValues>
					{multiple
						? value.length === 0 && title
							? title
							: value.map((v) => (
									<SelectValueBadge
										className={joinClassNames(disabled && 'disabled')}
										key={v as string}
										onClick={
											!disabled
												? (e) => {
														e.stopPropagation();
														selectOption(v);
												  }
												: undefined
										}>
										{getValue(v) as string}
									</SelectValueBadge>
							  ))
						: value === undefined && title
						? title
						: (getValue(value) as string)}
				</SelectValues>
				{!disabled &&
					((multiple && value.length > 0) ||
						(!multiple && value && value !== -1)) && (
						<SelectClearAll
							onClick={(e) => {
								e.stopPropagation();
								clearOptions();
							}}
							variant={variant}>
							&times;
						</SelectClearAll>
					)}
				<SelectDivider
					variant={variant}
					className={joinClassNames(
						_required && 'required',
						disabled && 'disabled'
					)}
				/>
				<SelectCaret
					variant={variant}
					className={joinClassNames(
						_required && 'required',
						disabled && 'disabled'
					)}
				/>
				{options.length > 0 && (
					<SelectOptions open={isOpen} ref={optionsRef}>
						{multiple && options.length > 0 && (
							<SelectOptionsAll
								id='list-actions'
								onClick={(e) => {
									e.stopPropagation();
								}}>
								<Button
									style={{
										borderRadius: '.25em',
									}}
									onClick={(e) => {
										setValue(options.map((o) => o.value));
									}}>
									All
								</Button>
								<Button
									style={{
										borderRadius: '.25em',
									}}
									onClick={(e) => {
										setValue([]);
									}}>
									None
								</Button>
							</SelectOptionsAll>
						)}
						{customRender
							? customRender({
									selectOption,
									isOptionSelected,
									highlightedIndex,
									setHighlightedIndex,
							  })
							: options.map((option, index) => (
									<SelectOption
										key={option.value as string}
										onMouseEnter={() => setHighlightedIndex(index)}
										className={joinClassNames(
											isOptionSelected(option) && 'selected',
											highlightedIndex === index && 'highlighted'
										)}
										onClick={(e) => {
											e.stopPropagation();
											selectOption(option.value);
											closeWhenSelect && setIsOpen(false);
										}}>
										{option.label}
									</SelectOption>
							  ))}
					</SelectOptions>
				)}
			</SelectContainer>
		</Column>
	);
};

export default SelectField;
